<template>
  <div class="form-field" :class="fieldClasses">
    <!-- Label -->
    <label 
      v-if="label"
      :for="fieldId"
      class="field-label"
      :class="labelClasses"
    >
      {{ label }}
      <span v-if="required" class="required-marker" aria-label="Required">*</span>
    </label>
    
    <!-- Description -->
    <div v-if="description" class="field-description">
      {{ description }}
    </div>
    
    <!-- Input wrapper with validation -->
    <div class="field-input">
      <slot 
        :field-id="fieldId"
        :has-error="hasError"
        :error-messages="errorMessages"
        :show-validation-icon="showValidationIcon"
        :validate="handleValidate"
      />
    </div>
    
    <!-- Help text -->
    <div v-if="helpText && !hasError" class="field-help">
      <Icon name="info" :size="12" class="help-icon" />
      {{ helpText }}
    </div>
    
    <!-- Error messages -->
    <div 
      v-if="errorMessages.length > 0" 
      :id="`${fieldId}-errors`"
      class="field-errors"
      role="alert"
      aria-live="polite"
    >
      <div 
        v-for="(error, index) in errorMessages" 
        :key="index" 
        class="field-error"
      >
        <Icon name="error" :size="12" class="error-icon" />
        {{ error }}
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, provide } from 'vue';
import Icon from '@/components/atoms/Icon.vue';
import { useFieldValidation, type ValidationRule } from '@/composables';

export interface FormFieldProps {
  label?: string;
  description?: string;
  helpText?: string;
  required?: boolean;
  disabled?: boolean;
  fieldId?: string;
  size?: 'small' | 'medium' | 'large';
  showValidationIcon?: boolean;
  // Validation props
  modelValue?: any;
  validationRules?: ValidationRule[];
  validateOnBlur?: boolean;
  validateOnInput?: boolean;
  // Layout props
  layout?: 'vertical' | 'horizontal';
  labelWidth?: string;
}

const props = withDefaults(defineProps<FormFieldProps>(), {
  required: false,
  disabled: false,
  fieldId: () => `field-${Math.random().toString(36).substr(2, 9)}`,
  size: 'medium',
  showValidationIcon: false,
  validationRules: () => [],
  validateOnBlur: true,
  validateOnInput: false,
  layout: 'vertical'
});

const emit = defineEmits<{
  'update:modelValue': [value: any];
  'validate': [isValid: boolean, errors: string[]];
}>();

// Use field validation if rules are provided
const fieldValidation = props.validationRules.length > 0 
  ? useFieldValidation(props.modelValue, props.validationRules)
  : null;

// Computed properties
const hasError = computed(() => 
  fieldValidation ? !fieldValidation.isValid.value : false
);

const errorMessages = computed(() => 
  fieldValidation ? fieldValidation.errors.value : []
);

const fieldClasses = computed(() => [
  `form-field--${props.size}`,
  `form-field--${props.layout}`,
  {
    'form-field--required': props.required,
    'form-field--disabled': props.disabled,
    'form-field--error': hasError.value,
    'form-field--with-validation': props.showValidationIcon
  }
]);

const labelClasses = computed(() => ({
  'label--required': props.required,
  'label--disabled': props.disabled
}));

// Validation handler
const handleValidate = (value?: any) => {
  if (fieldValidation) {
    const actualValue = value !== undefined ? value : props.modelValue;
    fieldValidation.value.value = actualValue;
    
    const isValid = fieldValidation.validate();
    emit('validate', isValid, fieldValidation.errors.value);
    
    return isValid;
  }
  
  emit('validate', true, []);
  return true;
};

// Watch for model value changes if using validation
if (fieldValidation) {
  // Sync external model value with internal validation state
  if (props.modelValue !== undefined) {
    fieldValidation.value.value = props.modelValue;
  }
}

// Provide field context to child components
provide('formFieldContext', {
  fieldId: props.fieldId,
  hasError,
  errorMessages,
  showValidationIcon: props.showValidationIcon,
  required: props.required,
  disabled: props.disabled,
  size: props.size,
  validate: handleValidate
});
</script>

<style scoped lang="scss">
@use '@/styles/variables';
@use '@/styles/mixins';

.form-field {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-sm);
  width: 100%;
  
  &--small {
    gap: var(--spacing-xs);
    font-size: 12px;
  }
  
  &--medium {
    gap: var(--spacing-sm);
    font-size: var(--vscode-font-size);
  }
  
  &--large {
    gap: var(--spacing-md);
    font-size: 14px;
  }
  
  &--horizontal {
    flex-direction: row;
    align-items: flex-start;
    gap: var(--spacing-lg);
    
    .field-label {
      flex-shrink: 0;
      width: 150px;
      margin-top: var(--spacing-xs); // Align with input text
    }
    
    .field-input,
    .field-description,
    .field-help,
    .field-errors {
      flex: 1;
    }
  }
  
  &--disabled {
    opacity: 0.6;
    pointer-events: none;
  }
  
  &--error .field-label {
    color: var(--vscode-inputValidation-errorForeground);
  }
}

.field-label {
  font-weight: 600;
  color: var(--vscode-foreground);
  font-size: inherit;
  line-height: 1.4;
  
  &--disabled {
    color: var(--vscode-foreground);
    opacity: 0.6;
  }
}

.required-marker {
  color: var(--vscode-inputValidation-errorForeground);
  margin-left: var(--spacing-xs);
  font-weight: normal;
}

.field-description {
  font-size: 12px;
  color: var(--vscode-foreground);
  opacity: 0.8;
  line-height: 1.4;
  margin-top: calc(var(--spacing-sm) * -0.5); // Reduce gap between label and description
}

.field-input {
  display: flex;
  flex-direction: column;
}

.field-help {
  display: flex;
  align-items: flex-start;
  gap: var(--spacing-xs);
  font-size: 12px;
  color: var(--vscode-foreground);
  opacity: 0.7;
  line-height: 1.4;
}

.help-icon {
  color: var(--vscode-settings-inlineActionsForeground);
  flex-shrink: 0;
  margin-top: 1px; // Align with text baseline
}

.field-errors {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xs);
}

.field-error {
  display: flex;
  align-items: flex-start;
  gap: var(--spacing-xs);
  font-size: 12px;
  color: var(--vscode-inputValidation-errorForeground);
  background-color: var(--vscode-inputValidation-errorBackground);
  border: 1px solid var(--vscode-inputValidation-errorBorder);
  border-radius: var(--border-radius);
  padding: var(--spacing-xs) var(--spacing-sm);
  line-height: 1.4;
}

.error-icon {
  color: var(--vscode-inputValidation-errorForeground);
  flex-shrink: 0;
  margin-top: 1px; // Align with text baseline
}

// Horizontal layout adjustments
.form-field--horizontal {
  .field-description {
    margin-top: 0;
  }
}

// Responsive adjustments
@media (max-width: 768px) {
  .form-field--horizontal {
    flex-direction: column;
    gap: var(--spacing-sm);
    
    .field-label {
      width: auto;
      margin-top: 0;
    }
  }
}
</style>
